import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.mjs';
import React from 'react';
import { styled, css, keyframes } from 'styled-components';
import { KLoadingBalls } from '../KLoadingBalls/KLoadingBalls.mjs';
import { focusOutline } from '../../utils/focusStyles.mjs';

// These could maybe be moved to the theme
const UI_ORANGE_05 = '#FCF0E1';
const UI_ORANGE_70 = '#A03800';
const Button = styled.button.withConfig({
  displayName: "KButton__Button",
  componentId: "sc-1idja9j-0"
})(["--height:", ";display:flex;justify-content:center;align-items:center;height:var(--height);border-radius:calc(0.47 * var(--height));cursor:pointer;font-weight:700;padding:0;background:transparent;border-style:solid;border-width:0;transition:all 100ms ease-out;transition-property:border,background,color,padding;flex-shrink:0;position:relative;overflow:hidden;&[disabled]{pointer-events:none;}&:focus-visible,&.focus-visible{", "}", " ", " ", ";svg{pointer-events:none;}"], _ref => {
  let {
    $size
  } = _ref;
  return $size === 'large' ? '56px' : '44px';
}, focusOutline, _ref2 => {
  let {
    $variant,
    $size
  } = _ref2;
  switch ($variant) {
    case 'primary':
    case 'primary-inverted':
      return css`
          padding: ${$size === 'large' ? '0 24px' : '0 16px'};

          &:active {
            border-top-width: 4px;
          }
        `;
    case 'secondary':
    case 'secondary-inverted':
      return css`
          border-width: 2px;
          padding: ${$size === 'large' ? '0 22px' : '0 14px'};

          &:active {
            border-top-width: 4px;
            padding-top: 2px;
          }
        `;
  }
}, _ref3 => {
  let {
    $variant,
    theme: {
      k: {
        color
      }
    },
    $visuallyDisabled
  } = _ref3;
  switch ($variant) {
    case 'primary':
      return css`
          color: ${color.text.inverted};
          background: ${color.kBrand.kOrange};
          border-color: ${UI_ORANGE_70};

          &:hover,
          &:active {
            background: ${color.text.kOrange};
          }

          ${$visuallyDisabled && css`
            background: ${color.fill.tertiary};
          `}
        `;
    case 'secondary':
      return css`
          background: ${color.bg.primary};
          color: ${color.kBrand.kOrange};
          border-color: ${color.kBrand.kOrange};

          &:hover,
          &:active {
            background: ${UI_ORANGE_05};
            color: ${color.text.kOrange};
          }

          ${$visuallyDisabled && css`
            color: ${color.text.quinary};
            border-color: ${color.fill.tertiary};
          `}
        `;
    case 'tertiary':
      return css`
          color: ${color.text.kOrange};
          border-radius: 8px;

          &:hover {
            text-decoration: underline;
            text-decoration-thickness: 2px;
            text-underline-offset: 2px;
          }

          &:active {
            padding-top: 4px;
          }

          ${$visuallyDisabled && css`
            color: ${color.text.quinary};
          `}
        `;
    case 'primary-inverted':
      return css`
          background: ${color.bg.primary};
          border-color: rgb(40 40 40 / 0.7);

          /* This causes the content to have the same color as the background behind the button */
          color: black;
          mix-blend-mode: screen;

          &:hover,
          &:active {
            background: rgb(255 255 255 / 0.8);
          }

          ${$visuallyDisabled && css`
            opacity: 0.7;
            color: ${color.text.tertiary};
          `}

          &:focus-visible,
          &.focus-visible {
            /* Custom focus outline to avoid blend mode affecting the blue color */
            box-shadow:
              0 0 0 2px black,
              0 0 0 4px #fff !important;
          }
        `;
    case 'secondary-inverted':
      return css`
          color: ${color.text.inverted};
          border-color: ${color.bg.primary};

          &:hover,
          &:active {
            background: rgb(40 40 40 / 0.2);
          }

          ${$visuallyDisabled && css`
            opacity: 0.6;
          `}

          &:focus-visible,
          &.focus-visible {
            /* This is for consistency with the primary-inverted focus outline */
            mix-blend-mode: screen;
            box-shadow:
              0 0 0 2px black,
              0 0 0 4px #fff !important;
          }
        `;
  }
}, _ref4 => {
  let {
    $shape
  } = _ref4;
  switch ($shape) {
    case 'squircle':
      return css`
          width: var(--height);
          padding: 0;
        `;
  }
});
const Content = styled.div.withConfig({
  displayName: "KButton__Content",
  componentId: "sc-1idja9j-1"
})(["display:flex;justify-content:center;align-items:center;gap:", ";transition:opacity 200ms ease-in-out,transform 200ms ease-in-out;opacity:1;", ""], _ref5 => {
  let {
    $size
  } = _ref5;
  return $size === 'large' ? '12px' : '8px';
}, _ref6 => {
  let {
    $loading
  } = _ref6;
  return $loading && css`
      opacity: 0;
      transform: translateY(100%);
      width: auto;
    `;
});
const Text = styled.div.withConfig({
  displayName: "KButton__Text",
  componentId: "sc-1idja9j-2"
})(["font-weight:bold;font-size:", ";line-height:", ";display:flex;align-items:center;white-space:nowrap;user-select:none;"], _ref7 => {
  let {
    theme
  } = _ref7;
  return theme.k.fontSize.text.l;
}, _ref8 => {
  let {
    theme
  } = _ref8;
  return theme.k.lineHeight.text.l;
});
const Icon = styled.div.withConfig({
  displayName: "KButton__Icon",
  componentId: "sc-1idja9j-3"
})(["flex-shrink:0;display:flex;align-items:center;width:24px;height:24px;", " svg,img{width:100%;height:auto;pointer-events:none;}"], _ref9 => {
  let {
    $variant,
    $size
  } = _ref9;
  return $variant !== 'tertiary' && css`
      &:first-child {
        margin-left: ${$size === 'large' ? '-8px' : '-4px'};
      }

      &:last-child {
        margin-right: ${$size === 'large' ? '-8px' : '-4px'};
      }
    `;
});
const loadingIndicatorAppear = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }

  100% {
    transform: translateY(0%);
    opacity: 1;
  }
`;
const LoadingIndicator = styled(KLoadingBalls).withConfig({
  displayName: "KButton__LoadingIndicator",
  componentId: "sc-1idja9j-4"
})(["position:absolute;animation:", " 200ms ease-in-out both;animation-delay:150ms;color:currentcolor;"], loadingIndicatorAppear);
const KButton = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    icon,
    iconRight,
    children,
    variant = 'primary',
    size: sizeProp = 'large',
    shape = 'default',
    loading,
    ...rest
  } = props;
  const disabled = rest.disabled || loading;
  const visuallyDisabled = rest.disabled;

  // Tertiary variant always has small size
  const size = variant === 'tertiary' ? 'small' : sizeProp;
  return /*#__PURE__*/React.createElement(Button, _extends({}, rest, {
    $variant: variant,
    $size: size,
    $shape: shape,
    ref: ref,
    disabled: disabled,
    $visuallyDisabled: visuallyDisabled
  }), /*#__PURE__*/React.createElement(Content, {
    $size: size,
    $loading: loading
  }, icon && /*#__PURE__*/React.createElement(Icon, {
    $variant: variant,
    $size: size
  }, icon), children && /*#__PURE__*/React.createElement(Text, null, children), iconRight && /*#__PURE__*/React.createElement(Icon, {
    $variant: variant,
    $size: size
  }, iconRight)), loading && /*#__PURE__*/React.createElement(LoadingIndicator, null));
});
KButton.displayName = 'KButton';

export { KButton };
